/**
 * Exponential Backoff 재시도 유틸리티
 *
 * @example 함수 직접 호출
 * ```ts
 * const result = await retryWithBackoff(() => fetch('/api/stream'), {
 *   maxRetries: 10,
 *   baseDelay: 2000,
 *   maxDelay: 30000,
 * });
 * ```
 *
 * @example React Hook 사용
 * ```ts
 * const { execute, reset, state } = useRetryWithBackoff({
 *   fn: () => hlsConnect(url),
 *   maxRetries: 10,
 *   baseDelay: 2000,
 *   onSuccess: () => console.log('connected'),
 *   onRetry: (attempt, delay) => console.log(`retry ${attempt} in ${delay}ms`),
 *   onFailed: () => console.log('all retries exhausted'),
 * });
 * ```
 */

// ── 타입 ──

export interface RetryOptions {
  /** 최대 재시도 횟수 (기본: 5) */
  maxRetries?: number;
  /** 초기 대기 시간 ms (기본: 2000) */
  baseDelay?: number;
  /** 최대 대기 시간 ms (기본: 30000) */
  maxDelay?: number;
  /** 배수 (기본: 2) */
  multiplier?: number;
  /** jitter 비율 0~1 (기본: 0.2 = ±20%) */
  jitter?: number;
  /** 재시도할 에러인지 판별 (기본: 모든 에러 재시도) */
  shouldRetry?: (error: unknown, attempt: number) => boolean;
}

export interface RetryState {
  /** 현재 상태 */
  status: 'idle' | 'running' | 'retrying' | 'success' | 'failed';
  /** 현재 재시도 횟수 (0 = 첫 시도) */
  attempt: number;
  /** 최대 재시도 횟수 */
  maxRetries: number;
  /** 다음 재시도까지 남은 시간 ms (retrying 상태에서만) */
  nextRetryIn: number;
  /** 마지막 에러 */
  lastError: unknown | null;
}

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxRetries: 5,
  baseDelay: 2000,
  maxDelay: 30000,
  multiplier: 2,
  jitter: 0.2,
  shouldRetry: () => true,
};

// ── 지연 시간 계산 ──

/**
 * Exponential backoff 지연 시간 계산
 * delay = min(baseDelay × multiplier^attempt, maxDelay) ± jitter
 */
export function calculateBackoffDelay(
  attempt: number,
  options?: Pick<RetryOptions, 'baseDelay' | 'maxDelay' | 'multiplier' | 'jitter'>,
): number {
  const {
    baseDelay = DEFAULT_OPTIONS.baseDelay,
    maxDelay = DEFAULT_OPTIONS.maxDelay,
    multiplier = DEFAULT_OPTIONS.multiplier,
    jitter = DEFAULT_OPTIONS.jitter,
  } = options ?? {};

  const exponentialDelay = baseDelay * Math.pow(multiplier, attempt);
  const cappedDelay = Math.min(exponentialDelay, maxDelay);

  // jitter 적용: ±jitter 범위의 랜덤 변동
  const jitterRange = cappedDelay * jitter;
  const jitterOffset = (Math.random() * 2 - 1) * jitterRange;

  return Math.round(Math.max(0, cappedDelay + jitterOffset));
}

// ── Promise 기반 재시도 함수 ──

/**
 * Exponential backoff으로 비동기 함수를 재시도합니다.
 * API 호출, fetch 등에 직접 사용할 수 있습니다.
 *
 * @param fn - 실행할 비동기 함수
 * @param options - 재시도 옵션
 * @param onRetry - 재시도 시 호출되는 콜백 (attempt, delay)
 * @returns fn의 반환값
 * @throws 모든 재시도 실패 시 마지막 에러
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options?: RetryOptions,
  onRetry?: (attempt: number, delay: number, error: unknown) => void,
): Promise<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  let lastError: unknown;

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (attempt >= opts.maxRetries) break;
      if (!opts.shouldRetry(error, attempt)) break;

      const delay = calculateBackoffDelay(attempt, opts);
      onRetry?.(attempt + 1, delay, error);

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

// ── 취소 가능한 재시도 컨트롤러 ──

/**
 * 취소 가능한 exponential backoff 컨트롤러.
 * Hook이나 컴포넌트에서 수동 제어가 필요할 때 사용합니다.
 *
 * @example
 * ```ts
 * const controller = createRetryController(
 *   () => connectHLS(url),
 *   { maxRetries: 10, baseDelay: 2000 },
 * );
 *
 * controller.onStateChange = (state) => setRetryState(state);
 * controller.start();
 *
 * // 취소
 * controller.cancel();
 *
 * // 리셋 후 재시작
 * controller.reset();
 * controller.start();
 * ```
 */
export interface RetryController<T> {
  /** 재시도 시작 */
  start: () => void;
  /** 현재 진행 중인 재시도 취소 */
  cancel: () => void;
  /** 상태 리셋 (attempt, error 초기화) */
  reset: () => void;
  /** 현재 상태 조회 */
  getState: () => RetryState;
  /** 상태 변경 콜백 */
  onStateChange?: (state: RetryState) => void;
  /** 성공 콜백 */
  onSuccess?: (result: T) => void;
  /** 재시도 콜백 */
  onRetry?: (attempt: number, delay: number, error: unknown) => void;
  /** 최종 실패 콜백 */
  onFailed?: (error: unknown) => void;
}

export function createRetryController<T>(
  fn: () => Promise<T>,
  options?: RetryOptions,
): RetryController<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  let state: RetryState = {
    status: 'idle',
    attempt: 0,
    maxRetries: opts.maxRetries,
    nextRetryIn: 0,
    lastError: null,
  };

  let timer: ReturnType<typeof setTimeout> | null = null;
  let countdownTimer: ReturnType<typeof setInterval> | null = null;
  let cancelled = false;

  const controller: RetryController<T> = {
    onStateChange: undefined,
    onSuccess: undefined,
    onRetry: undefined,
    onFailed: undefined,

    getState: () => ({ ...state }),

    start: () => {
      cancelled = false;
      attempt(0);
    },

    cancel: () => {
      cancelled = true;
      if (timer) { clearTimeout(timer); timer = null; }
      if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null; }
      setState({ ...state, status: 'idle', nextRetryIn: 0 });
    },

    reset: () => {
      controller.cancel();
      state = {
        status: 'idle',
        attempt: 0,
        maxRetries: opts.maxRetries,
        nextRetryIn: 0,
        lastError: null,
      };
      controller.onStateChange?.(state);
    },
  };

  function setState(newState: RetryState) {
    state = newState;
    controller.onStateChange?.(state);
  }

  async function attempt(attemptNum: number) {
    if (cancelled) return;

    setState({
      ...state,
      status: attemptNum === 0 ? 'running' : 'retrying',
      attempt: attemptNum,
      nextRetryIn: 0,
    });

    try {
      const result = await fn();
      if (cancelled) return;
      setState({ ...state, status: 'success', lastError: null, nextRetryIn: 0 });
      controller.onSuccess?.(result);
    } catch (error) {
      if (cancelled) return;

      state.lastError = error;

      if (attemptNum >= opts.maxRetries || !opts.shouldRetry(error, attemptNum)) {
        setState({ ...state, status: 'failed', lastError: error, nextRetryIn: 0 });
        controller.onFailed?.(error);
        return;
      }

      const delay = calculateBackoffDelay(attemptNum, opts);
      controller.onRetry?.(attemptNum + 1, delay, error);

      // countdown 표시
      let remaining = delay;
      setState({ ...state, status: 'retrying', attempt: attemptNum + 1, nextRetryIn: remaining });

      countdownTimer = setInterval(() => {
        remaining = Math.max(0, remaining - 1000);
        setState({ ...state, nextRetryIn: remaining });
        if (remaining <= 0 && countdownTimer) {
          clearInterval(countdownTimer);
          countdownTimer = null;
        }
      }, 1000);

      timer = setTimeout(() => {
        if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null; }
        attempt(attemptNum + 1);
      }, delay);
    }
  }

  return controller;
}
